import React from 'react';

import { DashboardCard } from './DashboardCard';
import { ProjectRow } from './ProjectRow';
import type { DashboardContext, DashboardProject } from './useDashboardData';

interface MyProjectsCardProps {
  projects: DashboardProject[];
  /**
   * True total of projects reachable via "View all projects" (/expert_requests).
   * Drives the badge so it matches that list; `projects` is the active-projects
   * preview shown in the card body. ENG-5253.
   */
  total: number;
  /** Cost per project, in display cents (USD or OFC pip per dashCtx). */
  costByProjectCents: Map<string, number>;
  dashCtx: DashboardContext;
  attentionCount: number;
  loading: boolean;
  error?: boolean;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
}

export function MyProjectsCard({
  projects,
  total,
  costByProjectCents,
  dashCtx,
  attentionCount,
  loading,
  error,
  collapseButton,
  collapsed,
}: MyProjectsCardProps): React.ReactNode {
  const attentionBadge =
    attentionCount > 0 ? (
      <span className="inline-flex items-center rounded-full bg-yellow-500/15 px-8 py-2 text-[1.1rem] font-semibold text-yellow-800 dark:text-yellow-300">
        {attentionCount} need attention
      </span>
    ) : undefined;

  return (
    <DashboardCard
      title="My Projects"
      badge={total}
      attentionBadge={attentionBadge}
      linkText="View all projects"
      linkTo="/expert_requests"
      loading={loading}
      error={error}
      fullWidth
      collapseButton={collapseButton}
      collapsed={collapsed}
    >
      {projects.length === 0 ? (
        <p className="text-12 text-on-surface-muted">No active projects.</p>
      ) : (
        <div className="-mx-16 -mb-16 overflow-hidden rounded-b-lg border-t border-surface-border">
          {projects.map((project) => (
            <ProjectRow
              key={project.id}
              project={project}
              costCents={costByProjectCents.get(project.id) ?? 0}
              dashCtx={dashCtx}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </DashboardCard>
  );
}
